import { DialogPortal } from "#dialog-primitives";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { NativeOnlyAnimatedView } from "@-/dev.improve.haniverse.components.ui.native-only-animated-view";
import { cn } from "@-/com.npmjs.cn";
import { Close, Content, Description, Overlay, Title } from "@rn-primitives/dialog";
import { Fragment, type ComponentProps, type ReactNode } from "react";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { Platform, View, type ViewProps } from "@-/com.npmjs.react-native";
import { FadeIn, FadeOut } from "@-/com.npmjs.react-native-reanimated";
import { FullWindowOverlay as RNFullWindowOverlay } from "@-/com.npmjs.react-native-screens";

const PLATFORM_COMPONENTS = {
  ["ios" as string]: RNFullWindowOverlay,
};
const FullWindowOverlay = PLATFORM_COMPONENTS[Platform.OS] ?? Fragment;

export function DialogOverlay({
  children,
  className,
  ...props
}: Omit<ComponentProps<typeof Overlay>, "asChild"> & {
  children?: ReactNode;
}) {
  return (
    <FullWindowOverlay>
      <Overlay
        className={cn(
          "absolute inset-0 flex items-center justify-center bg-black/50 p-2",
          Platform.select({
            web: "animate-in fade-in-0 fixed cursor-default [&>*]:cursor-auto",
          }),
          className,
        )}
        {...props}
        asChild={Platform.OS !== "web"}
      >
        <NativeOnlyAnimatedView entering={FadeIn.duration(200)} exiting={FadeOut.duration(150)}>
          <NativeOnlyAnimatedView entering={FadeIn.delay(50)} exiting={FadeOut.duration(150)}>
            <>{children}</>
          </NativeOnlyAnimatedView>
        </NativeOnlyAnimatedView>
      </Overlay>
    </FullWindowOverlay>
  );
}
export function DialogContent({
  children,
  className,
  portalHost,
  ...props
}: ComponentProps<typeof Content> & {
  portalHost?: string;
}) {
  return (
    <DialogPortal hostName={portalHost}>
      <DialogOverlay>
        <Content
          className={cn(
            "z-50 mx-4 flex w-full max-w-full min-w-0 shrink flex-col items-stretch gap-4 rounded-xs border border-border bg-background p-6 shadow-lg shadow-black/5 sm:mx-auto sm:max-w-lg",
            Platform.select({
              web: "animate-in fade-in-0 zoom-in-95 duration-200",
            }),
            className,
          )}
          {...props}
        >
          <>{children}</>
          <Close
            className={cn(
              "absolute top-4 right-4 rounded-xs opacity-70 active:opacity-100",
              Platform.select({
                web: "ring-offset-background focus:ring-ring data-[state=open]:bg-accent transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2",
              }),
            )}
            hitSlop={12}
          >
            <Icon
              className={cn("size-4 shrink-0 text-accent-foreground web:pointer-events-none")}
              name="LuX"
            />
            <Text className="sr-only font-sans">Close</Text>
          </Close>
        </Content>
      </DialogOverlay>
    </DialogPortal>
  );
}

export function DialogHeader({ className, ...props }: ViewProps) {
  return (
    <View className={cn("flex flex-col gap-2 text-center sm:text-left", className)} {...props} />
  );
}

export function DialogFooter({ className, ...props }: ViewProps) {
  return (
    <View
      className={cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className)}
      {...props}
    />
  );
}

export function DialogTitle({ className, ...props }: ComponentProps<typeof Title>) {
  return (
    <Title
      className={cn("font-sans text-lg leading-none font-semibold text-foreground", className)}
      {...props}
    />
  );
}

export function DialogDescription({ className, ...props }: ComponentProps<typeof Description>) {
  return (
    <Description className={cn("font-sans text-sm text-muted-foreground", className)} {...props} />
  );
}
